"use strict";
/**
 * PwaService - Handles all PWA and offline mode functionality
 * Includes: service worker registration, manifest linking, offline mode UI setup
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PwaService = void 0;
const modal_service_1 = require("./modal.service");
const storage_service_1 = require("./storage.service");
const export_service_1 = require("./export.service");
const qrcode_service_1 = require("./qrcode.service");
class PwaService {
    /**
     * Initialize PWA features for offline mode
     * Registers service worker, enables manifest, and sets up offline UI
     */
    static init() {
        // Initialize modal service first
        modal_service_1.ModalService.init();
        // Disable double-tap zoom on all touch devices
        document.addEventListener('dblclick', (e) => e.preventDefault(), { passive: false });
        // Check for app updates and reload if version changed
        this.checkForUpdates();
        if (!this.isOfflineMode()) {
            return;
        }
        console.log('Initializing PWA for offline mode');
        // Enable manifest for PWA install
        this.linkManifest();
        // Load QR code library for offline mode
        this.loadQRCodeLibrary();
        // Register service worker for offline support
        this.registerServiceWorker();
    }
    /**
     * Check if offline mode is activated via URL parameter or standalone PWA app
     */
    static isOfflineMode() {
        const params = new URLSearchParams(window.location.search);
        // Check URL parameter
        if (params.get('offline') !== null) {
            return true;
        }
        // Check if running as standalone PWA app (installed on home screen)
        // iOS uses navigator.standalone
        if (navigator.standalone === true) {
            return true;
        }
        // Android/Chrome uses display-mode: standalone media query
        if (window.matchMedia('(display-mode: standalone)').matches) {
            return true;
        }
        return false;
    }
    /**
     * Link the PWA manifest to enable app installation
     */
    static linkManifest() {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = 'manifest.json';
        document.head.appendChild(link);
    }
    /**
     * Check if app version has changed and reload if needed
     * This ensures users get the latest version automatically
     */
    static checkForUpdates() {
        const storedVersion = localStorage.getItem(this.VERSION_STORAGE_KEY);
        if (storedVersion && storedVersion !== APP_VERSION) {
            console.log(`App updated from ${storedVersion} to ${APP_VERSION}`);
            localStorage.setItem(this.VERSION_STORAGE_KEY, APP_VERSION);
            // Clear old caches to force fresh content
            if ('caches' in window) {
                caches.keys().then(cacheNames => {
                    cacheNames.forEach(cacheName => {
                        caches.delete(cacheName);
                    });
                });
            }
            // Hard reload to get latest version
            window.location.reload();
        }
        else if (!storedVersion) {
            // First load
            localStorage.setItem(this.VERSION_STORAGE_KEY, APP_VERSION);
        }
    }
    /**
     * Load QR code library from CDN for generating result codes
     */
    static loadQRCodeLibrary() {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js';
        document.head.appendChild(script);
    }
    /**
     * Register service worker for offline asset caching
     */
    static registerServiceWorker() {
        if ('serviceWorker' in navigator) {
            window.addEventListener('load', () => {
                navigator.serviceWorker
                    .register('./service-worker.js', {
                    scope: './'
                })
                    .then((registration) => {
                    console.log('ServiceWorker registered:', registration.scope);
                })
                    .catch((error) => {
                    console.log('ServiceWorker registration failed:', error);
                });
            });
        }
    }
    /**
     * Setup invisible export trigger in intro box (bottom left)
     */
    static setupExportTrigger(onExportTriggered) {
        const intro = document.getElementById('fcc-intro');
        if (intro) {
            const exportTrigger = document.createElement('div');
            exportTrigger.id = 'offline-export-trigger';
            exportTrigger.addEventListener('dblclick', (e) => {
                e.preventDefault();
                onExportTriggered();
            }, { passive: false });
            intro.appendChild(exportTrigger);
        }
    }
    /**
     * Setup auto-reset timer on results page
     * Timer starts when results page is displayed
     */
    static setupResultsPageControls(onReset) {
        this.onResetCallback = onReset;
    }
    /**
     * Start or restart the auto-reset timer (call when showing results page)
     */
    static startAutoResetTimer() {
        if (this.onResetCallback) {
            this.setupAutoResetTimer(this.onResetCallback);
        }
    }
    /**
     * Setup 60-second auto-reset timer with callback
     */
    static setupAutoResetTimer(onTimeout) {
        // Clear any existing timer
        this.clearAutoResetTimer();
        const timerElement = document.getElementById('auto-reset-timer');
        let secondsRemaining = 60;
        if (timerElement) {
            timerElement.style.display = 'block';
            timerElement.textContent = `Resetting in ${secondsRemaining}s`;
            this.autoResetTimer = setInterval(() => {
                secondsRemaining--;
                timerElement.textContent = `Resetting in ${secondsRemaining}s`;
                if (secondsRemaining <= 0) {
                    this.clearAutoResetTimer();
                    onTimeout();
                }
            }, 1000);
        }
    }
    /**
     * Clear the auto-reset timer
     */
    static clearAutoResetTimer() {
        if (this.autoResetTimer) {
            clearInterval(this.autoResetTimer);
            this.autoResetTimer = null;
        }
        const timerElement = document.getElementById('auto-reset-timer');
        if (timerElement) {
            timerElement.style.display = 'none';
            timerElement.textContent = '';
        }
    }
    /**
     * Display results page in offline mode: start timer and generate QR code
     */
    static showResultsPage(resultsData) {
        // Start/restart the auto-reset timer
        this.startAutoResetTimer();
        // Generate QR code for sharing results
        qrcode_service_1.QRCodeService.generateResultsQRCode(resultsData.values, resultsData.savings, resultsData.country, resultsData.lang);
    }
    /**
     * Setup close button on export page
     */
    static setupExportPageControls() {
        const topSection = document.querySelector('#fcc-export .top');
        if (!topSection)
            return;
        let closeBtn = topSection.querySelector('#export-close');
        if (!closeBtn) {
            closeBtn = document.createElement('button');
            closeBtn.id = 'export-close';
            closeBtn.className = 'close-btn';
            closeBtn.textContent = '×';
            closeBtn.onclick = () => {
                window.location.href = '?offline=1';
            };
            topSection.appendChild(closeBtn);
        }
    }
    /**
     * Show modal with OK button only (offline mode only)
     */
    static showModal(message, title = 'Warning') {
        if (!this.isOfflineMode())
            return;
        modal_service_1.ModalService.show(message, title);
    }
    /**
     * Show confirmation modal with Cancel and Confirm buttons (offline mode only)
     */
    static showConfirmation(message, title, onConfirm) {
        if (!this.isOfflineMode())
            return;
        modal_service_1.ModalService.confirm(message, title, onConfirm);
    }
    /**
     * Populate export page with submissions data and setup buttons
     * Fetches submissions from storage and handles export/clear operations
     */
    static setupExportPage() {
        const submissions = storage_service_1.StorageService.getSubmissions();
        const info = storage_service_1.StorageService.getStorageInfo();
        // Update submission count
        const countElement = document.getElementById('export-count');
        if (countElement) {
            countElement.textContent = info.count.toString();
        }
        // Update app version
        const versionElement = document.getElementById('app-version');
        if (versionElement) {
            versionElement.textContent = APP_VERSION;
        }
        // Add close button
        this.setupExportPageControls();
        // Populate table
        this.populateExportTable(submissions);
        // Setup export CSV button
        const exportBtn = document.getElementById('export-csv');
        if (exportBtn) {
            exportBtn.onclick = () => {
                if (submissions.length === 0) {
                    modal_service_1.ModalService.show('No submissions to export');
                    return;
                }
                export_service_1.ExportService.exportToCSV(submissions);
                this.showModal(`Exported ${submissions.length} submissions`, 'Export complete');
            };
        }
        // Setup clear all button
        const clearBtn = document.getElementById('clear-submissions');
        if (clearBtn) {
            clearBtn.onclick = () => {
                modal_service_1.ModalService.confirm(`Delete all ${info.count} submissions? This cannot be undone.`, 'Confirm Delete', () => {
                    storage_service_1.StorageService.clearAll();
                    this.showModal('All submissions cleared', 'Cleared');
                    // Refresh after clearing
                    this.setupExportPage();
                });
            };
        }
    }
    /**
     * Populate submissions table with data
     */
    static populateExportTable(submissions) {
        const tableBody = document.querySelector('#submissions-table tbody');
        if (!tableBody)
            return;
        tableBody.innerHTML = '';
        submissions.forEach((submission) => {
            const row = document.createElement('tr');
            const date = new Date(submission.timestamp).toLocaleDateString();
            row.innerHTML = `
        <td>${date}</td>
        <td>${submission.contactData.first}</td>
        <td>${submission.contactData.last}</td>
        <td>${submission.contactData.email}</td>
        <td>${submission.contactData.phone}</td>
        <td>${submission.contactData.company}</td>
        <td>${submission.contactData.job}</td>
        <td>${submission.contactData.fleetSize}</td>
        <td>$${submission.results.total.toLocaleString()}</td>
      `;
            tableBody.appendChild(row);
        });
    }
}
exports.PwaService = PwaService;
// Version is injected at build time from package.json
PwaService.VERSION_STORAGE_KEY = 'fcc_app_version';
PwaService.autoResetTimer = null;
PwaService.onResetCallback = null;
